import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTABand from "../components/CTABand";
import PageMeta from "../components/PageMeta";

const STEPS = [
  { n: "01", title: "Requirement Assessment", line: "We understand your bed capacity, departments, clinical requirements, budget, location and project timeline.",
    items: ["Bed capacity", "Departments", "Clinical requirements", "Budget", "Location", "Project timeline"] },
  { n: "02", title: "Planning", line: "Support with equipment planning, department-wise requirements, procurement planning and infrastructure coordination.",
    items: ["Equipment planning", "Department-wise requirements", "Procurement planning", "Infrastructure coordination"] },
  { n: "03", title: "Procurement", line: "We source medical equipment, furniture, surgical equipment, consumables and supporting products.",
    items: ["Medical equipment", "Furniture", "Surgical equipment", "Consumables", "Supporting products"] },
  { n: "04", title: "Installation", line: "Support with delivery, installation, commissioning and user orientation.",
    items: ["Delivery", "Installation", "Commissioning", "User orientation"] },
  { n: "05", title: "Compliance Support", line: "Assistance related to licensing, regulatory requirements and accreditation preparation.",
    items: ["Licensing", "Regulatory requirements", "Accreditation preparation"] },
  { n: "06", title: "After-Sales", line: "Maintenance, repair, technical support, AMC support and replacement requirements.",
    items: ["Maintenance", "Repair", "Technical support", "AMC support", "Replacement requirements"] },
];

const HospitalSetup = () => (
  <main data-testid="hospital-setup-page">
    <PageMeta title="Complete Hospital Setup Solutions | AccuSurge" description="End-to-end hospital setup: requirement assessment, planning, procurement, installation, compliance support and after-sales — turnkey hospital project company in India." />
    <section className="bg-brand-950 pb-20 pt-40 md:pb-28 grain relative overflow-hidden">
      <div className="absolute -left-24 top-24 h-96 w-96 rounded-full bg-brand-600/25 blur-[130px]" aria-hidden="true" />
      <div className="container-x relative">
        <Reveal>
          <span className="eyebrow text-accent">Complete Hospital Setup Solutions</span>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl md:text-6xl font-semibold tracking-tighter text-white text-balance">
            From Healthcare Vision to Operational Facility.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70">
            One coordinated journey — requirement assessment to after-sales support — so your clinical team can focus on
            patients while we coordinate the project.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-accent" data-testid="setup-hero-plan">Plan Your Hospital <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact?intent=quote" className="btn-ghost-light">Request a Quote</Link>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-28">
      <div className="container-x">
        <Stagger>
          {STEPS.map((s) => (
            <StaggerItem key={s.n}>
              <div className="grid gap-6 border-t border-slate-200 py-12 last:border-b md:grid-cols-[140px_1fr_1fr] md:gap-12" data-testid={`setup-stage-${s.n}`}>
                <span className="font-mono text-5xl font-medium text-brand-200 md:text-6xl">{s.n}</span>
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-ink">{s.title}</h2>
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-slate-600">{s.line}</p>
                </div>
                <ul className="flex flex-wrap content-start gap-2">
                  {s.items.map((i) => <li key={i} className="chip !bg-slate-100 !border-transparent">{i}</li>)}
                </ul>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal className="mt-16">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-brand-900 p-10 md:flex-row md:items-center" data-testid="setup-narrative">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-white">Your idea, equipped and operational.</h2>
              <p className="mt-2 max-w-xl text-sm text-white/70">
                From the first sketch of a plan to the day your facility treats its first patient — and every service
                visit after — AccuSurge stays involved.
              </p>
            </div>
            <Link to="/contact" className="btn-accent shrink-0">Talk to Our Healthcare Expert</Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CTABand />
  </main>
);

export default HospitalSetup;
